import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';

// Utils
import { useTranslation } from 'next-i18next';
import { useMediaQuery } from 'react-responsive';
import ReactTooltip from 'react-tooltip';
import { addDays, subDays } from 'date-fns';
import { isEqual } from 'lodash';
import Tooltip from '../../../../atoms/Tooltip/Tooltip';
import { useAppContext } from '../../../../../src/store/AppContext';
import displayPrice from '../../../../../src/utils/displayPrice';
import usePrevious from '../../../../../src/hooks/usePrevious';
import updateCurrentPlayer from '../../../../../src/utils/booking/updateCurrentPlayer';
import getCampNumberOfDays from '../../../../../src/utils/package/getCampNumberOfDays';
import useWeekId from '../../../../../src/hooks/useWeekId';
import cn from '../../../../../src/utils/cn';

// Icons
import Cross from '../../../../../public/icons/cross.svg';

// Components
import OffersSlider from '../../../OffersSlider/OffersSlider';
import Button from '../../../../atoms/Button/Button';
import FlashMessage from '../../../../molecules/FlashMessage/FlashMessage';

import styles from './Offers.module.scss';

const SwitchTabs = dynamic(() => (import('../../../SwitchTabs/SwitchTabs')), { ssr: false });
const SimpleTabs = dynamic(() => (import('../../../SimpleTabs/SimpleTabs')), { ssr: false });

function Offers({
  pack,
  offersPanelIsOpened,
  setOffersPanelIsOpened,
  calcPanelHeight,
  listWasUpdated,
  isInsidePanel,
  setFirstSelectionDone,
  camps,
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [{ currentPlayer, players, cartUpdates }, stateDispatch] = useAppContext();
  const { getDateFromWeekId, getWeekIdFromDate, getOfferDatesFromWeekId } = useWeekId();

  const [tabsNeedUpdate, setTabsNeedUpdate] = useState(0);

  const [availableDates, setAvailableDates] = useState([]);
  const [otherAvailableDates, setOtherAvailableDates] = useState([]);

  const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);
  const prevSelection = usePrevious(selectedCheckboxes);
  const [otherSelectedCheckboxes, setOtherSelectedCheckboxes] = useState([]);
  const prevOtherSelection = usePrevious(otherSelectedCheckboxes);

  const handleAddPeriods = () => {
    // for other players than first player, on click on "add" => add all periods
    if (currentPlayer && currentPlayer.id !== 1) {
      const datesSelection = availableDates.map((date) => date.id);
      setSelectedCheckboxes(datesSelection);
    }
  };

  useEffect(() => {
    // if current player is not first player, apply common periods
    if (offersPanelIsOpened) {
      handleAddPeriods();
    }
  }, [offersPanelIsOpened]);

  const getAllowed = (selection, available) => {
    const allowed = [];
    if (selection?.length && available?.length) {
      selection.forEach((id) => {
        const date = available.find((d) => d.id === id);
        if (date) {
          if (getCampNumberOfDays(pack) > 7) {
            // for camps lasting more than one week (7 days)

            const start = getDateFromWeekId(date.weekId);
            const end = addDays(start, getCampNumberOfDays(pack) - 1);

            // get weekId of last week from the camp period
            const weekIdOfLastWeek = getWeekIdFromDate(end);

            // get date from where previous camp could begins and then get weekId from this date
            const weekIdCanStartFrom = getWeekIdFromDate(subDays(start, getCampNumberOfDays(pack)));

            allowed.push(weekIdOfLastWeek + 1, weekIdCanStartFrom);
          } else {
            // for camps lasting one week
            allowed.push(date.weekId + 1, date.weekId - 1);
          }
        }
      });
    }
    return allowed;
  };

  const getAvailability = (offersList, selection, available) => offersList.reduce((acc, offer) => {
    const isActiveOrPastWeek = offer.weekId <= getWeekIdFromDate(new Date());
    if (isActiveOrPastWeek) return acc;

    const availability = {
      id: `pack-${pack.id}-${offer.weekId}`,
      label: `${displayPrice(offer.campPrice / 100)} €`,
      sublabel: getOfferDatesFromWeekId({
        weekId: offer.weekId,
        isWeekend: pack.durationId === 2,
        days: pack.days,
        withShortMonth: true,
      }),
      flag: offer.flag,
      weekId: offer.weekId,
      disabled: selection?.length ? !getAllowed(selection, available).includes(offer.weekId) : false,
      price: offer.campPrice / 100,
    };
    return [...acc, availability];
  }, []);

  useEffect(() => {
    // on camp details page
    // if package is current player selected package, pre-select periods from current player selection
    if (!isInsidePanel
      && currentPlayer?.stage?.offers?.length
      && pack?.offers?.length
      && currentPlayer.stage.packageId === pack.id) {
      const packOffersWeekIds = pack.offers.map((o) => o.weekId);
      if (currentPlayer.stage.offers.every((offer) => packOffersWeekIds?.includes(offer.weekId))) {
        setSelectedCheckboxes(currentPlayer.stage.offers.map((o) => o.id));
      }
    }
  }, []);

  const handleSelection = (selection, available, updateDates) => {
    updateDates(available.map((d) => ({
      ...d,
      disabled: selection.length ? !getAllowed(selection, available).includes(d.weekId) : false,
    })));
  };

  useEffect(() => {
    handleSelection(selectedCheckboxes, availableDates, setAvailableDates);
  }, [selectedCheckboxes]);

  useEffect(() => {
    handleSelection(otherSelectedCheckboxes, otherAvailableDates, setOtherAvailableDates);
  }, [otherSelectedCheckboxes]);

  useEffect(() => {
    setAvailableDates(pack.offers?.length ? getAvailability(pack.offers, selectedCheckboxes, availableDates) : []);
  }, [pack.offers]);

  useEffect(() => {
    setOtherAvailableDates(pack.otherOffers?.length
      ? getAvailability(pack.otherOffers, otherSelectedCheckboxes, otherAvailableDates)
      : []);
  }, [pack.otherOffers]);

  const resetSelection = () => {
    setOffersPanelIsOpened(false);
    setSelectedCheckboxes([]);
    setOtherSelectedCheckboxes([]);
  };

  useEffect(() => {
    // reset selection if user changed camp from cart
    if (cartUpdates) {
      resetSelection();
      stateDispatch({ type: 'SET_CART_UPDATES', payload: false });
    }
  }, [cartUpdates]);

  useEffect(() => {
    // reset selection in the list but preserve cart
    if (setFirstSelectionDone) {
      setFirstSelectionDone(false);
    }
    setOffersPanelIsOpened(false);
  }, [listWasUpdated]);

  useEffect(() => {
    setTabsNeedUpdate((update) => update + 1);
  }, [availableDates, otherAvailableDates]);

  useEffect(() => {
    if (currentPlayer) {
      const weeksSelection = [];
      const selectedOffers = [];

      // user selected periods
      if (selectedCheckboxes.length) {
        selectedCheckboxes.forEach((box) => {
          const date = availableDates.find((d) => d.id === box);
          if (date) {
            weeksSelection.push(date.weekId);
            selectedOffers.push(date);
          }
        });

        // other periods
      } else if (otherSelectedCheckboxes.length) {
        otherSelectedCheckboxes.forEach((box) => {
          const date = otherAvailableDates.find((d) => d.id === box);
          if (date) {
            weeksSelection.push(date.weekId);
            selectedOffers.push(date);
          }
        });
      }

      // get stored on init
      const stored = (prevSelection === undefined && prevOtherSelection === undefined) ? currentPlayer.stage : null;

      const updatedPlayer = {
        ...currentPlayer,
        stage: weeksSelection.length ? {
          weeks: weeksSelection,
          packageId: pack.id,
          campId: pack.campId,
          productId: (Array.isArray(camps) ? camps : Object.values(camps || {}))
            .find((c) => c.id === pack.campId)?.product?.id || null,
          packageName: pack.name,
          durationId: pack.durationId,
          offers: selectedOffers,
          days: getCampNumberOfDays(pack),
          durationInDays: pack.days,
        } : stored,
        options: stored ? currentPlayer.options : null,
        otherOptions: stored ? currentPlayer.otherOptions : null,
      };
      updateCurrentPlayer(players, updatedPlayer, stateDispatch);
    }

    ReactTooltip.rebuild();
  }, [selectedCheckboxes, otherSelectedCheckboxes]);

  const allSelectedAreSoldOut = useCallback((list) => list.every((item) => item.flag === 'SOLD_OUT'), []);

  const getOtherPlayersAddComponent = () => (currentPlayer?.id !== 1 ? (
    <div className="m-l-20 m-b-20 mobile-m-l-0 mobile-m-t-20 mobile-m-b-10">
      {currentPlayer && !currentPlayer.stage
      && availableDates?.length > 0
      && isEqual(availableDates?.map((date) => date.weekId), players?.[0]?.stage?.offers?.map((offer) => offer.weekId))
        ? (
          <Button
            onClick={handleAddPeriods}
            text="uppercase"
            size={!isMobile ? 'small' : null}
            classNames="mobile-w-100"
          >
            {t('camp.periods.select')}
          </Button>
        ) : null}
    </div>
  ) : null);

  return (
    <div className="flex d-col a-stretch flex-auto">
      <div
        className={cn([
          'flex j-spb a-center p-20',
          isInsidePanel ? '' : 'mobile-p-b-0',
          isInsidePanel && isMobile ? styles.mobile_panel : '',
        ])}
      >
        {isInsidePanel ? (
          <h3 className="fs-16 fw-500 mobile-fs-18">
            {allSelectedAreSoldOut(availableDates) ? t('package.availableDates') : t('package.choosePeriod') }
          </h3>
        ) : (
          <h3 className="fs-16 fw-500">
            {isMobile ? (
              t('package.selectDate')
            ) : (
              allSelectedAreSoldOut(availableDates) ? t('package.availableDates') : t('package.choosePeriod')
            )}
          </h3>
        )}
        {!isMobile && isInsidePanel ? (
          <button
            type="button"
            onClick={resetSelection}
            className={`${styles.cancel} flex a-center text-c-secondary fs-14 fw-500 c-pointer`}
          >
            <span className="m-r-10">{t('common.cancel')}</span>
            <Cross width={16} height={16} />
          </button>
        ) : null}
      </div>

      {currentPlayer && currentPlayer.id !== 1 ? (
        <div className="p-l-20 p-r-20 p-b-20 mobile-p-b-10 mobile-m-t-20">
          <FlashMessage message={t('booking.mutliplayers.periodsWarning')} color="primary" />
        </div>
      ) : null}

      <div className="flex d-col a-stretch flex-auto">
        {allSelectedAreSoldOut(availableDates) ? (
          // SELECTED DATES SOLD OUT ------------
          isMobile ? (
            // MOBILE ------
            <div className="p-t-20 p-r-20 p-l-20">
              <div className="flex m-b-20">
                <FlashMessage message={t('package.outOfStockDates')} />
              </div>

              <OffersSlider
                checkboxes={otherAvailableDates}
                selection={otherSelectedCheckboxes}
                setSelection={setOtherSelectedCheckboxes}
                emptyOther={setSelectedCheckboxes}
                isMobile
              />
              {isInsidePanel ? (
                <div className={styles.add_btn}>
                  <Button
                    full
                    text="uppercase"
                    onClick={() => setOffersPanelIsOpened(false)}
                    disabled={selectedCheckboxes.length === 0 && otherSelectedCheckboxes.length === 0}
                  >
                    {t('common.addToCart')}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : (
            // DESKTOP ------
            <div className={`${styles.dates_slider} m-l-20 p-b-20 m-r-20`}>
              <OffersSlider
                checkboxes={otherAvailableDates}
                selection={otherSelectedCheckboxes}
                setSelection={setOtherSelectedCheckboxes}
                emptyOther={setSelectedCheckboxes}
              />
            </div>
          )
        ) : (
          // SELECTED DATES AVAILABLE ------------
          isMobile ? (
            // MOBILE ------
            <div className="flex d-col a-stretch p-t-20 p-r-20 p-l-20 flex-auto">
              <SwitchTabs
                switchTabsClassname="flex-auto"
                id={`switchTabsOffers_pack${pack.id}`}
                tabs={[t('package.offers.yourDates'), t('package.offers.otherDates')]}
                tabsContent={[
                  <OffersSlider
                    key="1"
                    checkboxes={availableDates}
                    selection={selectedCheckboxes}
                    setSelection={setSelectedCheckboxes}
                    emptyOther={setOtherSelectedCheckboxes}
                    isMobile
                  />,
                  <OffersSlider
                    key="2"
                    checkboxes={otherAvailableDates}
                    selection={otherSelectedCheckboxes}
                    setSelection={setOtherSelectedCheckboxes}
                    emptyOther={setSelectedCheckboxes}
                    isMobile
                  />,
                ]}
              />

              {isInsidePanel ? (
                <div className={cn([styles.add_btn])}>
                  <Button
                    full
                    text="uppercase"
                    onClick={() => setOffersPanelIsOpened(false)}
                    disabled={!(selectedCheckboxes.length + otherSelectedCheckboxes.length)}
                  >
                    {t('common.addToCart')}
                  </Button>
                </div>
              ) : (
                getOtherPlayersAddComponent()
              )}
            </div>
          ) : (
            // DESKTOP ------
            <>
              <SimpleTabs
                className={styles.simple_tabs}
                needStyleUpdate={tabsNeedUpdate}
                sliderId={pack.id}
                tabs={[t('package.offers.yourDates'), t('package.offers.otherDates')]}
                calcPanelHeight={calcPanelHeight}
                tabsContent={[
                  <div key="1" className={styles.dates_slider}>
                    <OffersSlider
                      checkboxes={availableDates}
                      selection={selectedCheckboxes}
                      setSelection={setSelectedCheckboxes}
                      emptyOther={setOtherSelectedCheckboxes}
                    />
                  </div>,
                  <div key="2" className={styles.dates_slider}>
                    <OffersSlider
                      checkboxes={otherAvailableDates}
                      selection={otherSelectedCheckboxes}
                      setSelection={setOtherSelectedCheckboxes}
                      emptyOther={setSelectedCheckboxes}
                    />
                  </div>,
                ]}
              />
              {!isInsidePanel ? (
                getOtherPlayersAddComponent()
              ) : null}
            </>
          )
        )}
        <Tooltip id="needUncheck" effect="solid" />
      </div>
    </div>
  );
}

Offers.propTypes = {
  setOffersPanelIsOpened: PropTypes.func,
  calcPanelHeight: PropTypes.func,
  listWasUpdated: PropTypes.number,
  isInsidePanel: PropTypes.bool,
  setFirstSelectionDone: PropTypes.func,
};

Offers.defaultProps = {
  setOffersPanelIsOpened: () => {},
  calcPanelHeight: () => {},
  isInsidePanel: false,
  listWasUpdated: 0,
  setFirstSelectionDone: null,
};

export default Offers;
